import { ParticipantProfile } from "../../../models/entities/participant.model";
import React, { useEffect, useState } from "react";
import { Form } from "react-final-form";
import ParticipantDetailsCard from "./ParticipantDetailsCard";
import { EndpointsService } from "../../../services/endpoints/endpoints.service";
import { FormApi } from "final-form";
import { PtDetailsMode } from "../../../models/pages/participant-details/participant-details.model";
import UiButton from "../../../components/button/Button";
import UiInputText from "../../../components/input-text/InputText.component";
import { Validations } from "../../../services/form/validations.service";
import ParticipantDetailsField from "./ParticipantDetailsField";
import { Formatter } from "../../../services/formatter/formatter.service";
import UiSelect from "../../../components/select/Select.component";
import { ListOption } from "../../../models/misc.model";
import { useTranslation } from "react-i18next";
import { ParticipantContextProps } from "../../../models/contexts/participant-context.model";
import {
	PtEhrMrnInfo,
	PtEhrMrnInfoForm,
} from "../../../models/pages/participant-details/ehr-mrn-info.model";
import { EhrProvider } from "../../../models/entities/ehr-provider.model";
import { useQuery } from "@tanstack/react-query";

const ParticipantDetailsEhrMrn = ({
	participant,
	participantContext,
}: {
	participant: ParticipantProfile;
	participantContext: ParticipantContextProps;
}) => {
	const { t } = useTranslation("common");
	const [mode, setMode] = useState<PtDetailsMode>(PtDetailsMode.READ);
	const [showError, setShowError] = useState<boolean>(false);
	const [formData, setFormData] = useState<PtEhrMrnInfoForm>({
		data: [],
	});
	const [newMrnData, setNewMrnData] = useState<PtEhrMrnInfo | null>(null);

	// All EHR providers from parent context (shared with ParticipantProfile)
	const allEhrProviders = participantContext.allEhrProviders;

	// Fetch available EHR providers for this participant (to determine if we can add)
	const { data: availableEhrProviders } = useQuery({
		queryKey: ["available-ehr-providers", participant.id],

		queryFn: async () => {
			return await EndpointsService.dataRetriever.getAvailableEhrProviders({
				participantId: participant.id,
			});
		},
	});

	useEffect(() => {
		setFormData({
			data: participant.ehrProviderParticipants
				? participant.ehrProviderParticipants.map((x) => new PtEhrMrnInfo(x))
				: [],
		});
	}, [participant.ehrProviderParticipants]);

	// Get EHR providers for dropdown (all providers, excluding ones already saved for this participant)
	const getEhrProvidersForDropdown = (): EhrProvider[] => {
		if (!allEhrProviders) return [];
		// Only exclude EHR providers that are already saved (in formData.data)
		// Don't exclude the one being selected in the current form - it should remain visible until saved
		const usedEhrProviderIds = new Set(
			formData.data.map((x) => x.ehrProviderId).filter((id) => id !== null)
		);
		// Filter out EHR providers that are already associated with this participant
		return allEhrProviders.filter((provider) => !usedEhrProviderIds.has(provider.id));
	};

	const add = () => {
		const newMrn = new PtEhrMrnInfo({
			participantId: participant.id,
		});
		newMrn.ehrProviderId = null;
		newMrn.providerParticipantId = null;
		setNewMrnData(newMrn);
		setMode(PtDetailsMode.UPDATE);
	};

	const cancelAdd = () => {
		setNewMrnData(null);
		setMode(PtDetailsMode.READ);
		setShowError(false);
	};

	const handleSubmit = async (
		data: PtEhrMrnInfoForm,
		form: FormApi<PtEhrMrnInfoForm, Partial<PtEhrMrnInfoForm>>
	) => {
		setShowError(false);

		if (!newMrnData || !newMrnData.ehrProviderId || !newMrnData.providerParticipantId) {
			setShowError(true);
			return;
		}

		await EndpointsService.dataRetriever
			.addEhrProviderParticipant({
				participantId: participant.id,
				body: {
					ehrProviderId: newMrnData.ehrProviderId,
					providerParticipantId: newMrnData.providerParticipantId,
				},
			})
			.then(() => {
				setNewMrnData(null);
				setMode(PtDetailsMode.READ);
				setShowError(false);
				form.reset();
				participantContext.onRefresh();
			})
			.catch(() => setShowError(true));
	};

	const ehrProvidersForDropdown = getEhrProvidersForDropdown();
	const canAdd = ehrProvidersForDropdown?.length > 0;
	const isAdding = newMrnData !== null && mode === PtDetailsMode.UPDATE;
	return (
		<Form
			key={`form-${isAdding ? "adding" : "read"}-${newMrnData?.ehrProviderId || ""}`}
			onSubmit={handleSubmit}
			initialValues={
				{
					...formData,
					newMrn: {
						ehrProviderId: newMrnData?.ehrProviderId
							? newMrnData.ehrProviderId.toString()
							: null,
						providerParticipantId: newMrnData?.providerParticipantId || "",
					},
				} as any
			}
			render={({ handleSubmit, form, submitting }) => (
				<form onSubmit={handleSubmit}>
					<ParticipantDetailsCard
						title="PARTICIPANT.DETAILS.EHR_MRN.TITLE"
						submitting={submitting}
						mode={mode}
						enableEditing={true}
						showError={showError}
						enableSubmit={
							isAdding &&
							newMrnData !== null &&
							newMrnData.ehrProviderId !== null &&
							newMrnData.providerParticipantId !== null &&
							newMrnData.providerParticipantId.trim() !== ""
						}
						onCancel={cancelAdd}
						onModeChange={() => {
							// When Update button is clicked, only switch to edit mode (enable Add button)
							setMode(PtDetailsMode.UPDATE);
						}}
					>
						{/* EXISTING DATA */}
						{formData.data.length > 0 ? (
							<>
								{formData.data.map((x, index) => {
									const ehrProvider = allEhrProviders?.find(
										(p) => p.id === x.ehrProviderId
									);
									return (
										<div
											id={index.toString()}
											key={index.toString()}
										>
											<div className="row align-items-center">
												<div className="col-12">
													<div className="row">
														{/* EHR Provider Name */}
														<div className="col-12 col-sm-6 col-md-6 col-lg-6">
															<ParticipantDetailsField
																title="PARTICIPANT.DETAILS.EHR_MRN.FORM_FIELDS.ehr"
																value={
																	ehrProvider?.name ||
																	Formatter.NO_DATA
																}
															/>
														</div>

														{/* Medical Record Number */}
														<div className="col-12 col-sm-6 col-md-6 col-lg-6">
															<ParticipantDetailsField
																title="PARTICIPANT.DETAILS.EHR_MRN.FORM_FIELDS.providerParticipantId"
																value={
																	x.providerParticipantId ||
																	Formatter.NO_DATA
																}
															/>
														</div>
													</div>
												</div>
											</div>
											<hr />
										</div>
									);
								})}
							</>
						) : (
							<></>
						)}

						{/* NEW MRN FORM (only shown when adding) */}
						{isAdding && (
							<div className="row align-items-center mb-3">
								<div className="col-12">
									<div className="row">
										{/* EHR Provider Dropdown */}
										<div className="col-12 col-sm-6 col-md-6 col-lg-6">
											<UiSelect
												id="newMrn.ehrProviderId"
												name="newMrn.ehrProviderId"
												label="PARTICIPANT.DETAILS.EHR_MRN.FORM_FIELDS.ehr"
												onChange={(e) => {
													if (newMrnData) {
														const selectedValue =
															(e as any).target?.value ||
															(e as any).value;
														setNewMrnData({
															...newMrnData,
															ehrProviderId: selectedValue
																? Number(selectedValue)
																: null,
														});
													}
												}}
												disabled={submitting}
												filter={true}
												showClear={true}
												options={ehrProvidersForDropdown.map(
													(x) =>
														new ListOption({
															id: x.id.toString(),
															label: x.name,
														})
												)}
											/>
										</div>

										{/* Medical Record Number Input */}
										<div className="col-12 col-sm-6 col-md-6 col-lg-6">
											<UiInputText
												id="newMrn.providerParticipantId"
												name="newMrn.providerParticipantId"
												label="PARTICIPANT.DETAILS.EHR_MRN.FORM_FIELDS.providerParticipantId"
												onChange={(e) => {
													if (newMrnData) {
														setNewMrnData({
															...newMrnData,
															providerParticipantId: e.target.value,
														});
													}
												}}
												validations={[Validations.required]}
												disabled={submitting}
											/>
										</div>
									</div>
								</div>
							</div>
						)}

						<div className="d-flex justify-content-center mt-2">
							<UiButton
								id="addEhrMrn"
								type="button"
								className="p-button-sm p-button-rounded"
								label="PARTICIPANT.DETAILS.EHR_MRN.CREATE"
								icon={"pi pi-plus"}
								onClick={() => add()}
								disabled={
									submitting ||
									newMrnData !== null ||
									!canAdd ||
									mode === PtDetailsMode.READ
								}
							/>
						</div>
					</ParticipantDetailsCard>
				</form>
			)}
		/>
	);
};

export default ParticipantDetailsEhrMrn;
